import React from 'react';
import { Problem } from '../types/Problem';

interface ProblemPanelProps {
  problem?: Problem;
  onReportIssue?: () => void;
}

const ProblemPanel: React.FC<ProblemPanelProps> = ({ problem, onReportIssue }) => {
  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy':
        return 'bg-green-100 text-green-800';
      case 'Medium':
        return 'bg-orange-100 text-orange-800';
      case 'Hard':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getEstimatedTime = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy':
        return '10-15 min';
      case 'Medium':
        return '15-20 min';
      case 'Hard':
        return '20-25 min';
      default:
        return '20 min';
    }
  };
  if (!problem) {
    return (
      <div className='h-[calc(100vh-100px)] p-6 bg-white rounded-lg shadow-lg overflow-y-auto flex items-center justify-center'>
        <div className='text-lg text-gray-600'>Loading problem...</div>
      </div>
    );
  }

  return (
    <div className='h-[calc(100vh-100px)] p-6 bg-white rounded-lg shadow-lg overflow-y-auto'>
      <div className='flex justify-between items-start mb-6 pb-4 border-b border-gray-200'>
        <div className='flex-1'>
          <h2 className='text-2xl font-semibold text-gray-800 mb-2'>
            {problem.title}
          </h2>
          <div className='flex items-center gap-3 mb-3'>
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${getDifficultyColor(
                problem.difficulty
              )}`}
            >
              {problem.difficulty}
            </span>
            <span className='text-sm text-gray-500'>
              Est. {getEstimatedTime(problem.difficulty)}
            </span>
          </div>
        </div>
        
        {/* Report Button */}
        {onReportIssue && (
          <div className='ml-4'>
            <button
              onClick={onReportIssue}
              className='flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors border border-gray-200 hover:border-orange-200'
              title='Report an issue with this problem'
            >
              <svg
                className='w-4 h-4'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z'
                />
              </svg>
              Report
            </button>
          </div>
        )}
      </div>

      <div className='space-y-6'>
        <div>
          <h3 className='text-lg font-semibold text-gray-800 mb-3'>
            Description
          </h3>
          <p className='text-gray-600 leading-relaxed'>{problem.description}</p>
        </div>

        <div>
          <h3 className='text-lg font-semibold text-gray-800 mb-3'>Examples</h3>
          {problem.examples.map((example, index) => (
            <div
              key={index}
              className='bg-gray-50 p-4 rounded-lg mb-4 border-l-4 border-blue-500'
            >
              <h4 className='text-blue-600 font-semibold mb-2 text-sm'>
                Example {index + 1}:
              </h4>
              <div className='space-y-2 text-sm font-mono'>
                <div>
                  <strong>Input:</strong> {example.input}
                </div>
                <div>
                  <strong>Output:</strong> {example.output}
                </div>
                {example.explanation && (
                  <div>
                    <strong>Explanation:</strong> {example.explanation}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {problem.constraints.length > 0 && (
          <div>
            <h3 className='text-lg font-semibold text-gray-800 mb-3'>
              Constraints
            </h3>
            <ul className='space-y-1 ml-6'>
              {problem.constraints.map((constraint, index) => (
                <li
                  key={index}
                  className='text-sm font-mono text-gray-600 list-disc'
                >
                  {constraint}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProblemPanel;
